import { Box } from "@mantine/core";
import {
  IconInfoCircle,
  IconAlertTriangle,
  IconCheck,
  IconBulb,
} from "@tabler/icons-react";
import type { ReactElement, ReactNode } from "react";
import { InlineMarkdown } from "./InlineMarkdown";

type CalloutType = "info" | "warning" | "success" | "tip";

interface CalloutProps {
  type?: CalloutType;
  title?: string;
  children: ReactNode;
}

const CONFIG: Record<
  CalloutType,
  { icon: ReactElement; accent: string; bg: string; border: string; defaultTitle: string }
> = {
  info: {
    icon: <IconInfoCircle size={20} />,
    accent: "var(--signal-info)",
    bg: "var(--signal-info-bg)",
    border: "var(--signal-info-border)",
    defaultTitle: "Info",
  },
  warning: {
    icon: <IconAlertTriangle size={20} />,
    accent: "var(--signal-warn)",
    bg: "var(--signal-warn-bg)",
    border: "var(--signal-warn-border)",
    defaultTitle: "Warning",
  },
  success: {
    icon: <IconCheck size={20} />,
    accent: "var(--signal-success)",
    bg: "var(--signal-success-bg)",
    border: "var(--signal-success-border)",
    defaultTitle: "Success",
  },
  tip: {
    icon: <IconBulb size={20} />,
    accent: "var(--signal-tip)",
    bg: "var(--signal-tip-bg)",
    border: "var(--signal-tip-border)",
    defaultTitle: "Tip",
  },
};

export function Callout({ type = "info", title, children }: CalloutProps) {
  const cfg = CONFIG[type];

  return (
    <Box
      my="lg"
      p="md"
      style={{
        background: cfg.bg,
        border: `1px solid ${cfg.border}`,
        borderRadius: 10,
        color: "var(--ink-body)",
      }}
    >
      <Box
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          color: cfg.accent,
          fontWeight: 600,
          marginBottom: 6,
          fontSize: 14,
          letterSpacing: "0.02em",
        }}
      >
        {cfg.icon}
        <span>{title ?? cfg.defaultTitle}</span>
      </Box>
      <Box style={{ fontSize: 15, lineHeight: 1.65 }}>
        <InlineMarkdown>{children}</InlineMarkdown>
      </Box>
    </Box>
  );
}
